import type {IconProps} from '@primer/octicons-react'
import {Box, Button} from '@primer/react'
import {NestableActionMenu} from '@github-ui/nestable-action-menu'
import type {FunctionComponent, PropsWithChildren} from 'react'

export type EmptyPickerProps = {
  leadingIcon?: FunctionComponent<PropsWithChildren<IconProps>>
  hotKey?: string
  label: string
  anchorProps?: React.HTMLAttributes<HTMLElement>
  readonly?: boolean
  ariaLabelledBy?: string
  /**
   * Whether to render the empty picker as a nested menu item (true) versus a standalone button (false; default).
   */
  nested?: boolean
}

export const SharedEmptyPicker = ({
  leadingIcon,
  hotKey,
  label,
  anchorProps,
  readonly,
  nested,
  ariaLabelledBy,
}: EmptyPickerProps) => {
  const sharedProps = {'aria-disabled': readonly, disabled: readonly, 'aria-labelledby': ariaLabelledBy}

  if (nested) {
    return (
      <NestableActionMenu.MenuItemAnchor leadingVisual={leadingIcon} {...anchorProps} {...sharedProps}>
        {label}
      </NestableActionMenu.MenuItemAnchor>
    )
  }

  return (
    <Button
      sx={{borderStyle: 'dashed', fontWeight: 'normal', color: 'fg.muted'}}
      size={'small'}
      leadingVisual={leadingIcon}
      trailingVisual={
        hotKey
          ? () => (
              <Box
                sx={{
                  color: 'fg.muted',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: 'border.default',
                  p: '0px 4px',
                  borderRadius: '6px',
                }}
              >
                {hotKey}
              </Box>
            )
          : undefined
      }
      {...sharedProps}
      {...anchorProps}
    >
      {label}
    </Button>
  )
}

try{ SharedEmptyPicker.displayName ||= 'SharedEmptyPicker' } catch {}